<template>
  <el-dialog
    :visible.sync="internalVisible"
    :show-close="true"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :modal-append-to-body="true"
    :append-to-body="true"
    :lock-scroll="true"
    custom-class="upgrade-modal-dialog"
    width="480px"
    @close="handleClose"
  >
    <div class="upgrade-modal">
      <div class="eyebrow">Upgrade required</div>
      <h2 class="headline">{{ headline }}</h2>
      <p v-if="featureName" class="feature-line">
        <span class="feature-name">{{ featureName }}</span>
        is available on paid plans.
      </p>
      <p class="body-copy">
        Get insights, peer feedback, Happily Village, and more by upgrading.
        Send a request and our team will reach out to set you up.
      </p>
      <div class="cta-row">
        <button
          type="button"
          class="primary-cta"
          @click="onRequestUpgrade"
        >
          Request Pro upgrade
        </button>
        <button
          type="button"
          class="secondary-cta"
          @click="handleClose"
        >
          Maybe later
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'UpgradeModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    featureName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      // Editable copy — tweak freely while iterating.
      headline: 'Unlock the full Happily experience',
    };
  },
  computed: {
    internalVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        if (!val) {
          this.$emit('close');
        }
      },
    },
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    onRequestUpgrade() {
      this.$emit('close');
      const target = { path: '/billing', hash: '#upgrade' };
      const current = this.$router.currentRoute;
      if (current.path === '/billing') {
        // Already on billing — just update the hash to focus the form.
        if (current.hash !== '#upgrade') {
          this.$router.replace(target).catch(() => {});
        }
        this.$nextTick(() => {
          const el = document.getElementById('upgrade');
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
        return;
      }
      this.$router.push(target).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
/* Scoped wrapper — the :deep/::v-deep selectors below also reach the
   el-dialog shell that Element UI teleports. */

::v-deep .upgrade-modal-dialog {
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid #ece8f4;
  box-shadow:
    0 1px 2px rgba(30, 10, 60, 0.04),
    0 20px 40px -24px rgba(78, 20, 172, 0.12);
  overflow: hidden;
}

::v-deep .upgrade-modal-dialog .el-dialog__header {
  padding: 0;
  border: none;
}

::v-deep .upgrade-modal-dialog .el-dialog__headerbtn {
  top: 16px;
  right: 16px;
}

::v-deep .upgrade-modal-dialog .el-dialog__body {
  padding: 0;
}

.upgrade-modal {
  padding: 36px 36px 32px;
  font-family: 'Lato', 'Kanit', sans-serif;
  color: #1a1a2e;
  user-select: none;

  .eyebrow {
    position: relative;
    display: inline-block;
    padding-left: 32px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #6C30CC;
    margin-bottom: 18px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 22px;
      height: 1px;
      background: #6C30CC;
      transform: translateY(-50%);
    }
  }

  .headline {
    font-family: 'Instrument Serif', Georgia, serif;
    font-style: italic;
    font-weight: 400;
    font-size: 28px;
    line-height: 1.2;
    color: #1a1a2e;
    margin: 0 0 16px;
    letter-spacing: -0.01em;
  }

  .feature-line {
    font-size: 15px;
    font-weight: 600;
    color: #4A1E8F;
    margin: 0 0 10px;
    line-height: 1.5;

    .feature-name {
      color: #6C30CC;
      font-weight: 800;
    }
  }

  .body-copy {
    font-size: 14px;
    font-weight: 400;
    color: #5a5a73;
    line-height: 1.6;
    margin: 0 0 28px;
  }

  .cta-row {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }

  .primary-cta {
    background: #6C30CC;
    color: #ffffff;
    border: none;
    border-radius: 12px;
    padding: 12px 20px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background 120ms ease, transform 120ms ease;

    &:hover {
      background: #5423A2;
    }

    &:active {
      transform: translateY(1px);
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid #FFC200;
      outline-offset: 2px;
    }
  }

  .secondary-cta {
    background: transparent;
    border: none;
    color: #6b7280;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 10px 4px;
    cursor: pointer;
    text-decoration: none;
    transition: color 120ms ease;

    &:hover {
      color: #1a1a2e;
      text-decoration: underline;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid #FFC200;
      outline-offset: 2px;
      border-radius: 6px;
    }
  }
}
</style>
